import { useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import InsightsIcon from "@mui/icons-material/Insights";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useGetMyStatisticsQuery } from "../../api/userApi";
import { useGetMyMonthLogQuery } from "../../api/dailyLogApi";
import { localDateString, monthKey as toMonthKey } from "../../lib/dateUtils";
import {
  last30Series,
  monthGoalRows,
  monthLabel,
  monthlySeries,
  previousMonthKey,
} from "../../lib/statsDashboard";
import { unitWord } from "../goals/goalRules";
import { kazaMarkers, muzakereMarkers } from "../../lib/dayMarkers";
import MonthCalendar from "../../components/ui/MonthCalendar";
import CalendarSkeleton from "../../components/ui/CalendarSkeleton";
import ProgressRing from "../../components/ui/ProgressRing";
import "../profile/darkSurface.css";

/** Grafik renkleri: tek ton (marka turkuazı); koyuda açık turkuaz, okunur eksen. */
function useChartTheme() {
  const dark = useTheme().palette.mode === "dark";
  return {
    dark,
    mark: dark ? "#8FC9CA" : "#006A71",
    markSoft: dark ? "rgba(143,201,202,0.35)" : "rgba(0,106,113,0.18)",
    perfect: dark ? "#FFFFFF" : "#48A6A7",
    grid: dark ? "rgba(255,255,255,0.08)" : "#E2E8F0",
    tick: { fontSize: 12, fill: dark ? "rgba(255,255,255,0.7)" : "#6C757D" },
    tooltip: {
      borderRadius: 12,
      border: dark ? "1px solid rgba(255,255,255,0.14)" : "1px solid #E2E8F0",
      background: dark ? "#123B3D" : "#FFFFFF",
      color: dark ? "#FFFFFF" : "#123B3D",
      fontSize: 13,
      boxShadow: "0 8px 24px -12px rgba(0,0,0,0.35)",
    },
  };
}

function Panel({ title, subtitle, right, className = "", bodyClassName = "", delay = 0, children }) {
  return (
    <section
      className={`rounded-2xl border border-terakkiBorder bg-terakkiSurface p-4 sm:p-5 flex flex-col gap-4 min-w-0 motion-safe:animate-rise-in dark:bg-white/5 dark:border-white/15 ${className}`}
      style={{ animationDelay: `${delay}s` }}
    >
      {title ? (
        <header className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-md font-SemiBold text-terakkiInk dark:text-white">{title}</h2>
            {subtitle ? <p className="text-base text-terakkiNeutral mt-0.5">{subtitle}</p> : null}
          </div>
          {right}
        </header>
      ) : null}
      <div className={`min-w-0 ${bodyClassName}`}>{children}</div>
    </section>
  );
}

function Kpi({ Icon, value, label, hint, loading, delay }) {
  return (
    <div
      className="rounded-2xl border border-terakkiBorder bg-terakkiSurface p-4 flex items-center gap-3 min-w-0 motion-safe:animate-rise-in dark:bg-white/5 dark:border-white/15"
      style={{ animationDelay: `${delay}s` }}
    >
      <span className="w-11 h-11 rounded-xl bg-terakkiBrandCloud text-terakkiPrimaryDark flex items-center justify-center shrink-0">
        <Icon aria-hidden />
      </span>
      <div className="min-w-0">
        {loading ? (
          <div className="h-8 w-16 rounded bg-terakkiSurfaceAlt animate-pulse" />
        ) : (
          <div className="text-4xl leading-none text-terakkiPrimaryDark font-SemiBold tabular-nums">{value}</div>
        )}
        <div className="text-base text-terakkiInk mt-1 leading-tight">{label}</div>
        {hint ? <div className="text-sm text-terakkiNeutral leading-tight">{hint}</div> : null}
      </div>
    </div>
  );
}

function Skeleton({ height }) {
  return <div className="w-full rounded-xl bg-terakkiSurfaceAlt animate-pulse" style={{ height }} />;
}

function Empty({ text }) {
  return <p className="text-base text-terakkiNeutral py-6 text-center">{text}</p>;
}

/** Son 30 gün: tek seri alan grafiği; üzerine gelince gün ve oran. */
function TrendChart({ data }) {
  const c = useChartTheme();
  return (
    <ResponsiveContainer width="100%" height={260}>
      <AreaChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
        <defs>
          <linearGradient id="stats-trend" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={c.mark} stopOpacity={0.35} />
            <stop offset="100%" stopColor={c.mark} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke={c.grid} />
        <XAxis dataKey="label" tick={c.tick} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={24} />
        <YAxis domain={[0, 100]} ticks={[0, 50, 100]} tick={c.tick} axisLine={false} tickLine={false} width={40} tickFormatter={(v) => `%${v}`} />
        <Tooltip
          contentStyle={c.tooltip}
          labelStyle={{ color: c.tooltip.color, fontWeight: 600 }}
          formatter={(v, _n, item) => [`%${v}${item?.payload?.perfect ? " · ★" : ""}`, "Tamamlama"]}
          cursor={{ stroke: c.mark, strokeOpacity: 0.3 }}
        />
        <Area
          type="monotone"
          dataKey="rate"
          stroke={c.mark}
          strokeWidth={2}
          fill="url(#stats-trend)"
          dot={false}
          activeDot={{ r: 5, stroke: c.dark ? "#123B3D" : "#FFFFFF", strokeWidth: 2 }}
          animationDuration={900}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/** Aylık gidiş: tek seri çubuk; seçili ay koyu, diğerleri açık ton. */
function MonthlyChart({ data, selected, onSelect }) {
  const c = useChartTheme();
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data.map((m) => ({ ...m, bar: m.rate > 0 ? m.rate : null }))} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke={c.grid} />
        <XAxis dataKey="label" tick={c.tick} axisLine={false} tickLine={false} />
        <YAxis domain={[0, 100]} ticks={[0, 50, 100]} tick={c.tick} axisLine={false} tickLine={false} width={40} tickFormatter={(v) => `%${v}`} />
        <Tooltip
          contentStyle={c.tooltip}
          labelStyle={{ color: c.tooltip.color, fontWeight: 600 }}
          formatter={(_v, _n, item) => [`%${item?.payload?.rate || 0} · ${item?.payload?.perfectDays || 0} tam gün`, "Ortalama"]}
          cursor={{ fill: c.markSoft, opacity: 0.4 }}
        />
        <Bar
          dataKey="bar"
          radius={[6, 6, 0, 0]}
          maxBarSize={44}
          animationDuration={800}
          onClick={(d) => d?.monthKey && onSelect(d.monthKey)}
          style={{ cursor: "pointer" }}
        >
          {data.map((m) => (
            <Cell key={m.monthKey} fill={m.monthKey === selected ? c.mark : c.markSoft} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Son 7 gün: gün gün ince sütunlar; tam gün yıldızlı. */
function WeekBars({ days }) {
  return (
    <div className="grid grid-cols-7 gap-2 sm:gap-3 items-end h-[200px]">
      {days.map((d, i) => {
        const rate = Math.round(Math.min(100, d.avgCompletion || 0));
        return (
          <div key={d.date} className="flex flex-col items-center gap-2 h-full min-w-0" title={`${d.dayName} · %${rate}`}>
            <span className="text-sm text-terakkiNeutral tabular-nums">%{rate}</span>
            <div className="relative flex-1 w-full max-w-[36px] rounded-lg bg-terakkiBrandMist dark:bg-white/10 overflow-hidden flex items-end">
              <div
                className={`w-full rounded-lg transition-[height] duration-700 ease-out ${
                  d.isPerfectDay ? "bg-terakkiPrimaryDark dark:bg-white" : "bg-terakkiPrimary dark:bg-terakkiBrandSoft"
                }`}
                style={{ height: `${Math.max(rate, 2)}%`, transitionDelay: `${i * 60}ms` }}
              />
            </div>
            <span className="text-base text-terakkiInk dark:text-white leading-none">
              {d.isPerfectDay ? "★" : (d.dayName || "").slice(0, 3)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Seçili ayın hedef dökümü: satır satır oran çubuğu, toplam ve ortalama. */
function GoalRows({ rows, t }) {
  return (
    <ul className="flex flex-col divide-y divide-terakkiBorder">
      {rows.map((g, i) => (
        <li key={g.key} className="py-3 first:pt-0 last:pb-0 flex flex-col gap-1.5 min-w-0">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-md text-terakkiInk dark:text-white break-words min-w-0">{g.label}</span>
            <span className="text-md font-SemiBold text-terakkiPrimaryDark tabular-nums shrink-0">%{g.rate}</span>
          </div>
          <div className="h-2 rounded-full bg-terakkiBrandMist dark:bg-white/10 overflow-hidden">
            <div
              className="h-full rounded-full bg-terakkiPrimaryDark dark:bg-white transition-[width] duration-700 ease-out"
              style={{ width: `${Math.min(100, g.rate)}%`, transitionDelay: `${i * 50}ms` }}
            />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-base text-terakkiNeutral">
            <span>
              {t("stats_goal_summary_total")}: {g.total.toLocaleString("tr-TR")}
              {g.unit ? ` ${unitWord(g.unit, t)}` : ""}
            </span>
            <span>
              {t("stats_goal_summary_avg")}: {Number(g.average || 0).toLocaleString("tr-TR")}
            </span>
            <span>
              {g.days} {t("stats_unit_days")}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * Kişisel istatistik panosu: özet sayılar, 30 günlük gidiş, son 7 gün,
 * seçili ayın hedef dökümü (o ay hangi hedefler varsa hepsi), aylık gidiş
 * ve ay takvimi. Sayılar sunucudan; boş geçen günler de sayılır.
 */
export default function StatisticsPage() {
  const { t } = useScriptMode();
  const today = localDateString(new Date());
  const currentKey = toMonthKey(new Date());
  const [selected, setSelected] = useState(currentKey);

  const { data: statsResp, isLoading: statsLoading } = useGetMyStatisticsQuery();
  const { data: curMonth, isLoading: curLoading } = useGetMyMonthLogQuery({ monthKey: currentKey });
  const { data: prevMonth, isLoading: prevLoading } = useGetMyMonthLogQuery({ monthKey: previousMonthKey(currentKey) });
  const selectedQ = useGetMyMonthLogQuery({ monthKey: selected });
  const selectedLoading = selectedQ.isLoading || (selectedQ.isFetching && !selectedQ.currentData);

  const stats = statsResp?.data || {};
  const summary = stats.summary || {};
  const trend = useMemo(
    () => last30Series([...(prevMonth?.data?.dailyLogs || []), ...(curMonth?.data?.dailyLogs || [])], today),
    [prevMonth, curMonth, today]
  );
  const months = useMemo(() => monthlySeries(stats.monthlyStats), [stats.monthlyStats]);
  // Döküm o ayın günlük kayıtlarından (takvimle aynı kaynak); sunucunun sayısı varsa o.
  const selectedLogs = selectedQ.currentData?.data?.dailyLogs;
  const goals = useMemo(
    () => monthGoalRows({ monthlyStats: stats.monthlyStats, monthKey: selected, dailyLogs: selectedLogs, today }),
    [stats.monthlyStats, selected, selectedLogs, today]
  );
  const selectedMonth = months.find((m) => m.monthKey === selected) || null;
  const [sy, sm] = selected.split("-").map(Number);
  const selectedDate = useMemo(() => new Date(sy, sm - 1, 1), [sy, sm]);

  const loading = statsLoading;
  const trendLoading = curLoading || prevLoading;
  const monthChips = [...months].reverse();

  return (
    <div className="tk-dark w-full flex flex-col gap-4 sm:gap-5">
      {/* Başlık ve ay seçimi */}
      <div className="flex flex-wrap items-end justify-between gap-3 motion-safe:animate-rise-in">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-11 h-11 rounded-xl bg-terakkiPrimaryDark text-white flex items-center justify-center shrink-0 dark:bg-white/10">
            <InsightsIcon aria-hidden />
          </span>
          <div className="min-w-0">
            <h1 className="text-3xl font-SemiBold text-terakkiPrimaryDark leading-tight">{t("stats_title")}</h1>
            <p className="text-base text-terakkiNeutral">{t("stats_subtitle")}</p>
          </div>
        </div>
        {monthChips.length > 0 ? (
          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label={t("stats_pick_month")}>
            {monthChips.map((m) => {
              const active = m.monthKey === selected;
              return (
                <button
                  key={m.monthKey}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setSelected(m.monthKey)}
                  className={`h-9 px-3.5 rounded-full text-base transition-colors ${
                    active
                      ? "bg-terakkiPrimaryDark text-white dark:bg-white dark:text-terakkiPrimaryDark"
                      : "bg-terakkiBrandCloud text-terakkiPrimaryDark hover:bg-terakkiBrandMist"
                  }`}
                >
                  {monthLabel(m.monthKey)}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* Özet sayılar */}
      <div className="grid grid-cols-1 min-[420px]:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
        <Kpi
          Icon={LocalFireDepartmentOutlinedIcon}
          loading={loading}
          value={summary.currentStreak ?? 0}
          label={t("stats_current_streak")}
          hint={t("stats_unit_days")}
          delay={0.04}
        />
        <Kpi
          Icon={EmojiEventsOutlinedIcon}
          loading={loading}
          value={summary.longestStreak ?? 0}
          label={t("stats_longest_streak")}
          hint={t("stats_unit_days")}
          delay={0.08}
        />
        <Kpi
          Icon={StarBorderRoundedIcon}
          loading={loading}
          value={summary.perfectDays ?? 0}
          label={t("stats_perfect_days")}
          hint={t("stats_perfect_last30", { count: summary.completedDaysInLast30 ?? 0 })}
          delay={0.12}
        />
        <div
          className="rounded-2xl border border-terakkiBorder bg-terakkiSurface p-4 flex items-center gap-3 min-w-0 motion-safe:animate-rise-in dark:bg-white/5 dark:border-white/15"
          style={{ animationDelay: "0.16s" }}
        >
          {loading ? (
            <span className="w-[64px] h-[64px] rounded-full bg-terakkiSurfaceAlt animate-pulse shrink-0" />
          ) : (
            <ProgressRing value={summary.completionRate || 0} size={64} stroke={7} color="#006A71" fillClassName="dark:stroke-white" />
          )}
          <div className="min-w-0">
            <div className="text-base text-terakkiInk leading-tight">{t("stats_completion_rate")}</div>
            <div className="text-sm text-terakkiNeutral leading-tight mt-0.5">{t("stats_completion_hint")}</div>
          </div>
        </div>
      </div>

      {/* 30 günlük gidiş, son 7 gün ve aylık gidiş: geniş ekranda tek satır */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-5 gap-4 sm:gap-5">
        <Panel title={t("stats_trend_title")} subtitle={t("stats_trend_subtitle")} className="lg:col-span-2" delay={0.2}>
          {trendLoading ? <Skeleton height={260} /> : trend.length === 0 ? <Empty text={t("stats_no_data")} /> : <TrendChart data={trend} />}
        </Panel>
        <Panel title={t("stats_week_title")} subtitle={t("stats_week_subtitle")} bodyClassName="flex-1 flex flex-col justify-end" delay={0.24}>
          {loading ? <Skeleton height={200} /> : <WeekBars days={stats.weeklyStats || []} />}
        </Panel>
        <Panel
          title={t("stats_monthly_title")}
          subtitle={t("stats_monthly_subtitle")}
          className="xl:col-span-2"
          bodyClassName="flex-1 flex flex-col justify-end"
          delay={0.28}
        >
          {loading ? (
            <Skeleton height={220} />
          ) : months.length === 0 ? (
            <Empty text={t("stats_no_data")} />
          ) : (
            <MonthlyChart data={months} selected={selected} onSelect={setSelected} />
          )}
        </Panel>
      </div>

      {/* Seçili ayın hedefleri ve takvimi: yan yana, aynı yükseklikte; liste kendi içinde kayar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-12 gap-4 sm:gap-5">
        <Panel
          title={t("stats_goals_title", { month: monthLabel(selected) })}
          subtitle={t("stats_goals_subtitle")}
          className="xl:col-span-7 lg:h-[560px]"
          bodyClassName="flex-1 min-h-0 overflow-y-auto pr-1"
          delay={0.32}
          right={
            selectedMonth ? (
              <span className="text-base text-terakkiPrimaryDark bg-terakkiBrandCloud rounded-full px-3 py-1 tabular-nums">
                %{selectedMonth.rate} · {selectedMonth.perfectDays} ★
              </span>
            ) : null
          }
        >
          {loading || selectedLoading ? (
            <div className="flex flex-col gap-3">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} height={44} />
              ))}
            </div>
          ) : goals.length === 0 ? (
            <Empty text={t("stats_no_data")} />
          ) : (
            <GoalRows rows={goals} t={t} />
          )}
        </Panel>
        {/* Seçili ayın takvimi */}
        <Panel
          title={monthLabel(selected)}
          subtitle={t("stats_calendar_subtitle")}
          className="xl:col-span-5 lg:h-[560px]"
          bodyClassName="flex-1 min-h-0 lg:overflow-y-auto pr-1"
          delay={0.36}
        >
          {selectedLoading ? (
            <CalendarSkeleton weeks={5} offset={0} />
          ) : (
            <MonthCalendar
              monthDate={selectedDate}
              dailyLogs={selectedQ.currentData?.data?.dailyLogs || []}
              muzakereMarks={muzakereMarkers(selectedQ.currentData?.data)}
              kazaMarks={kazaMarkers(selectedQ.currentData?.data)}
              today={new Date()}
            />
          )}
        </Panel>
      </div>
    </div>
  );
}
